import _ from "lodash";
import { useMemo } from "react";

import {
  collectionsListName,
  departmentsListName,
  sectorsListName,
  processesListName,
  typesListName,
  linesListName,
  ListFieldsRecordType,
  MDWorkstationListName,
} from "src/api/config";
import { Keys, RestKeys } from "src/pages/DashboardPage/acadia/type";
import { CheckBoxListItem } from "src/types/main";
import { useAppSelector } from "./redux";
import { selectCurrentUser } from "src/modules/users/selector";
import { modifiedItemsState } from "src/modules/spItems/selectors";

interface WorkstationFilters {
  collections: CheckBoxListItem[];
  departments: CheckBoxListItem[];
}

const useListFilters = (workstationFilters?: WorkstationFilters) => {
  const currentUser = useAppSelector(selectCurrentUser);

  const obj = useAppSelector((state) => state.spitems.elements.obj);

  const collections = obj.Collections as Record<number, ListFieldsRecordType[typeof collectionsListName]>;
  const departments = obj.Departments as Record<number, ListFieldsRecordType[typeof departmentsListName]>;
  const sectors = obj.Sectors as Record<number, ListFieldsRecordType[typeof sectorsListName]>;
  const processes = obj.Processes as Record<number, ListFieldsRecordType[typeof processesListName]>;

  const types = obj.Types as Record<number, ListFieldsRecordType[typeof typesListName]>;

  const workstations = obj.MDWorkstation as Record<number, ListFieldsRecordType[typeof MDWorkstationListName]>;

  const lines = obj.Lines as Record<number, ListFieldsRecordType[typeof linesListName]>;

  const selectedCollectionIds = useMemo(
    () => (workstationFilters?.collections ?? []).map((c) => Number(c.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workstationFilters?.collections],
  );

  const selectedDepartmentIds = useMemo(
    () => (workstationFilters?.departments ?? []).map((d) => Number(d.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workstationFilters?.departments],
  );

  const filteredWorkstations = useMemo<CheckBoxListItem[]>(() => {
    if (!workstations) return [];

    return Object.values(workstations)
      .filter((ws) => {
        if (selectedCollectionIds.length > 0) {
          const locationId = Number(ws.Location?.get_lookupId?.());
          if (!selectedCollectionIds.includes(locationId)) return false;
        }
        if (selectedDepartmentIds.length > 0) {
          const departmentId = Number(ws.Department?.get_lookupId?.());
          if (!selectedDepartmentIds.includes(departmentId)) return false;
        }
        return true;
      })
      .map((ws) => ({
        id: ws.ID,
        title: (ws.Location ? ws.Location.get_lookupValue() + "_" : "") + ws.Workstation,
      }))
      .sort((a, b) => (a.title as string).localeCompare(b.title as string, "ru", { sensitivity: "base" }));
  }, [workstations, selectedCollectionIds, selectedDepartmentIds]);

  let collectionsState: any[] = modifiedItemsState(collections, collectionsListName);

  if (collectionsState.length) {
    // Фильтрация коллекций
    const filteredCollections = _.filter(collectionsState, (collection) => {
      // Если Hide: false, включаем коллекцию
      if (!collection.Hide) return true;

      // Если Hide: true, проверяем наличие пересечения групп
      return _.some(collection.Groups, (group) => _.some(currentUser.groups, { Id: group.ID }));
    });

    collectionsState = filteredCollections.map((obj) => ({
      id: obj.ID,
      title: obj.Title,
      code: obj.Code,
    }));
  }

  const data = {
    collections: {
      title: "Коллекция",
      data: collectionsState,
    },
    departments: {
      title: "Подразделение/Функция",
      data: departments
        ? Object.keys(departments).map((obj) => ({
            id: departments[+obj].ID,
            title: departments[+obj].Title,
            code: departments[+obj].Code,
          }))
        : [],
    },
    sectors: {
      title: "Колонна/Направление/Отдел",
      data: sectors
        ? Object.keys(sectors).map((obj) => ({
            id: sectors[+obj].ID,
            title: sectors[+obj].Title,
            code: sectors[+obj].Code,
          }))
        : [],
    },
    processes: {
      title: "Блок колонны/Участок",
      data: processes
        ? Object.keys(processes).map((obj) => ({
            id: processes[+obj].ID,
            title: processes[+obj].Title,
            code: processes[+obj].Code,
          }))
        : [],
    },
    types: {
      title: "Тип документа",
      data: types ? Object.keys(types).map((obj) => ({ id: types[+obj].ID, title: types[+obj].Title })) : [],
    },
    lines: {
      title: "Линия розлива",
      data: lines ? Object.keys(lines).map((obj) => ({ id: lines[+obj].ID, title: lines[+obj].Title })) : [],
    },
    critical: {
      title: "Критичность",
      data: [
        { id: 1, title: "Критичный" },
        { id: 0, title: "Не критичный" },
      ],
    },
    workstations: {
      title: "Рабочее место",
      data: filteredWorkstations,
    },
    // sap: {
    //   title: "SAP № технического места",
    //   data: equipmentsFilters ? equipmentsFilters.sap : [],
    // },
    // typeEquipment: {
    //   title: "Тип оборудования",
    //   data: equipmentsFilters ? equipmentsFilters.typeEquipment : [],
    // },
    // model: {
    //   title: "Модель оборудования",
    //   data: equipmentsFilters ? equipmentsFilters.model : [],
    // },
    // manufacturer: {
    //   title: "Производитель оборудования",
    //   data: equipmentsFilters ? equipmentsFilters.manufacturer : [],
    // },
    // titleEquipment: {
    //   title: "Название оборудования",
    //   data: equipmentsFilters ? equipmentsFilters.titleEquipment : [],
    // },
  };

  const baseData = _.omit(data, ["created", "responsibleForRevision", "dateForRevision"]) as Record<
    RestKeys | Keys,
    { title: string; data: CheckBoxListItem[] }
  >;

  return { baseData };
};

export default useListFilters;
